import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const WebSocketContext = createContext({
  sendMessage: () => {},
  readyState: WebSocket.CLOSED,
});

export const WebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const connectTimeoutRef = useRef(null);
  const pendingQueueRef = useRef([]);
  const [readyState, setReadyState] = useState(WebSocket.CLOSED);

  const WS_URL = "wss://api.insightos.co.uk/ws";
  const MAX_RECONNECT_DELAY = 30000; // cap exponential backoff

  const cleanupSocket = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch (err) {
        console.error("Error closing WebSocket", err);
      }
      socketRef.current = null;
    }
    setReadyState(WebSocket.CLOSED);
  };

  const flushQueue = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      while (pendingQueueRef.current.length) {
        const payload = pendingQueueRef.current.shift();
        socketRef.current.send(payload);
      }
    }
  };

  const connect = () => {
    cleanupSocket();
    const socket = new WebSocket(WS_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      setReadyState(WebSocket.OPEN);
      reconnectAttemptsRef.current = 0;

      pingIntervalRef.current = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);

      flushQueue();
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const customEvent = new CustomEvent("insightos-websocket-message", { detail: data });
        window.dispatchEvent(customEvent);
      } catch (err) {
        console.error("Failed to parse WebSocket message", err);
      }
    };

    socket.onclose = () => {
      setReadyState(WebSocket.CLOSED);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      // exponential backoff
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(1000 * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
      reconnectAttemptsRef.current = attempt + 1;
      connectTimeoutRef.current = setTimeout(connect, delay);
    };

    socket.onerror = (err) => {
      console.error("WebSocket error", err);
    };
  };

  useEffect(() => {
    connect();
    return () => {
      cleanupSocket();
    };
  }, []);

  const sendMessage = (message) => {
    try {
      const payload = typeof message === "string" ? message : JSON.stringify(message);
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(payload);
      } else {
        pendingQueueRef.current.push(payload);
      }
    } catch (err) {
      console.error("Failed to send WebSocket message", err);
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, readyState }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
